import React, { useRef, useState } from "react";
import Button from "../../../../core/Button";
import Input from "../../../../core/Inputs/DefaultInput";
import { Link } from "react-router-dom";
import Axios from "axios";
import Svg from "../../../../component/common/Svg";
import { toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';

const checks = [
  { key: 'length',  label: 'At least 12 characters',     test: (p) => p.length >= 12 },
  { key: 'upper',   label: 'Contains uppercase letter',   test: (p) => /[A-Z]/.test(p) },
  { key: 'lower',   label: 'Contains lowercase letter',   test: (p) => /[a-z]/.test(p) },
  { key: 'number',  label: 'Contains number',             test: (p) => /[0-9]/.test(p) },
  { key: 'special', label: 'Contains special character',  test: (p) => /[^A-Za-z0-9]/.test(p) },
];

const getStrength = (password) => {
  const passed = checks.filter(c => c.test(password)).length;
  if (!password) return { level: 0, label: '', color: '' };
  if (passed <= 1) return { level: 1, label: 'Weak',   color: '#e53935' };
  if (passed <= 2) return { level: 2, label: 'Fair',   color: '#fb8c00' };
  if (passed <= 3) return { level: 3, label: 'Fair',   color: '#fb8c00' };
  if (passed === 4) return { level: 4, label: 'Strong', color: '#43a047' };
  return                  { level: 5, label: 'Strong', color: '#43a047' };
};

const CheckIcon = () => (
  <svg width="14" height="10" viewBox="0 0 15 10" fill="none">
    <path d="M1 4.43027L5.72616 9L14 1" stroke="#16C748" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
  </svg>
);

const CrossIcon = () => (
  <svg width="14" height="14" viewBox="0 0 20 20" fill="none">
    <path d="M14.8477 4.94922L4.94816 14.8487" stroke="#F05454" strokeWidth="2" strokeLinecap="round"/>
    <path d="M14.8477 14.8496L4.94816 4.95011" stroke="#F05454" strokeWidth="2" strokeLinecap="round"/>
  </svg>
);

export const ResetPassword = () => {
  const inputRef = useRef();
  const [newpassword, setNewPassword] = useState('');
  const [renewpassword, setReNewPassword] = useState('');
  const [passwordType, setPasswordType] = useState('password');
  const [passwordConfirmType, setPasswordConfirmType] = useState('password');

  const urlParams = new URLSearchParams(window.location.search);
  let email = urlParams.get('email');

  React.useEffect(() => {
    if (email == null) {
      window.location = '/ForgotPassword';
    }
  });

  const strength = getStrength(newpassword);
  const passedChecks = checks.map(c => ({ ...c, passed: c.test(newpassword) }));
  const allValid = passedChecks.every(c => c.passed);
  const passwordsMatch = newpassword && renewpassword && newpassword === renewpassword;

  const setPassword = () => {
    if (!newpassword) {
      toast.error("Please enter a new password.");
      return;
    }
    if (!allValid) {
      toast.error("Password does not meet all requirements.");
      return;
    }
    if (!renewpassword) {
      toast.error("Please confirm your password.");
      return;
    }
    if (newpassword !== renewpassword) {
      toast.error("Passwords don't match!");
      return;
    }

    Axios.post(process.env.REACT_APP_NODE_API_URL + "/users/changePassword", {
      user_email: email,
      new_password: newpassword,
    }).then((response) => {
      if (response.data.statusCode === 200) {
        toast.success('Password changed successfully!');
        window.location = '/admin/password-changed/?email=' + email;
      } else {
        toast.error(response.data.msg || 'Something went wrong. Please try again.');
      }
    }).catch((error) => {
      const msg = error?.response?.data?.msg;
      toast.error(msg || 'Something went wrong. Please try again.');
    });
  };

  return (
    <div className='d-flex flex-column site-main-wrapper login-page'>
      <div className="flex-grow-1 main-content-wrap d-flex align-items-center justify-content-center">
        <div className="container">
          <div className="form-outer-wrapper">
            <div className="logo-wrap text-center">
              <Link to="/#0t" className='home-logo'>
                <Svg name="logo" />
              </Link>
            </div>
            <div className="form-wrapper">
              <div className="form-heading text-center">
                <h3>Reset password</h3>
                <p>Your new password must be different from previously used passwords.</p>
              </div>
              <div className="field-wrapper">

                {/* New Password */}
                <div className="mb__20">
                  <label style={{ fontWeight: 600, fontSize: 14, marginBottom: 6, display: 'block' }}>
                    New Password
                  </label>
                  <div className="position-relative">
                    <Input
                      ref={inputRef}
                      type={passwordType}
                      view="outlined"
                      color="default"
                      label=""
                      placeholder="Enter new password"
                      fill
                      className={"formsinput"}
                      name="newpassword"
                      value={newpassword}
                      onInput={e => setNewPassword(e.target.value)}
                    />
                    <span className={"forgot-wrap " + passwordType} onClick={() => setPasswordType(t => t === 'password' ? 'text' : 'password')}>
                      <i className="fa fa-eye" aria-hidden="true"></i>
                    </span>
                  </div>

                  {/* Strength Bar */}
                  {newpassword && (
                    <div style={{ marginTop: 10 }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 6 }}>
                        <span style={{ fontSize: 13, color: '#555', fontWeight: 500 }}>Password strength</span>
                        <span style={{ fontSize: 13, fontWeight: 600, color: strength.color }}>{strength.label}</span>
                      </div>
                      <div style={{ display: 'flex', gap: 4 }}>
                        {[1, 2, 3, 4, 5].map(seg => (
                          <div
                            key={seg}
                            style={{
                              flex: 1,
                              height: 6,
                              borderRadius: 3,
                              backgroundColor: seg <= strength.level ? strength.color : '#e0e0e0',
                              transition: 'background-color 0.3s',
                            }}
                          />
                        ))}
                      </div>

                      {/* Validation Checklist */}
                      <ul style={{ listStyle: 'none', padding: 0, margin: '10px 0 0 0' }}>
                        {passedChecks.map(c => (
                          <li
                            key={c.key}
                            style={{
                              fontSize: 13,
                              marginBottom: 4,
                              color: c.passed ? '#16C748' : '#F05454',
                              display: 'flex',
                              alignItems: 'center',
                              gap: 6,
                            }}
                          >
                            {c.passed ? <CheckIcon /> : <CrossIcon />}
                            {c.label}
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                </div>

                {/* Confirm Password */}
                <div className="mb__20">
                  <label style={{ fontWeight: 600, fontSize: 14, marginBottom: 6, display: 'block' }}>
                    Confirm Password
                  </label>
                  <div className="position-relative">
                    <Input
                      type={passwordConfirmType}
                      view="outlined"
                      color="default"
                      label=""
                      placeholder="Enter confirm password"
                      fill
                      className={"formsinput"}
                      name="renewpassword"
                      value={renewpassword}
                      onInput={e => setReNewPassword(e.target.value)}
                    />
                    <span className={"forgot-wrap " + passwordConfirmType} onClick={() => setPasswordConfirmType(t => t === 'password' ? 'text' : 'password')}>
                      <i className="fa fa-eye" aria-hidden="true"></i>
                    </span>
                  </div>

                  {/* Match Indicator */}
                  {renewpassword && (
                    <div style={{ marginTop: 6, fontSize: 13, display: 'flex', alignItems: 'center', gap: 6 }}>
                      {passwordsMatch ? (
                        <><CheckIcon /><span style={{ color: '#16C748', fontWeight: 500 }}>Passwords match</span></>
                      ) : (
                        <><CrossIcon /><span style={{ color: '#F05454', fontWeight: 500 }}>Passwords do not match</span></>
                      )}
                    </div>
                  )}
                </div>

              </div>
              <div className="button-wrap">
                <Button
                  className={"formsbutton"}
                  type="default"
                  view="filled"
                  color="primary"
                  text="Reset Password"
                  fill={true}
                  onClick={setPassword}
                />
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ResetPassword;
